import type { Mark } from "@tiptap/pm/model";
import type { EditorState } from "@tiptap/pm/state";

/**
 * Which Schriftgröße the toolbar shows as active (Anke, 2026-10-03, review
 * 0031/01). `editor.getAttributes("textStyle")` reads only the FIRST
 * `textStyle` mark of a range selection, so a selection over "normal" and
 * "groß" lit up "L". This reads the whole selection instead.
 *
 * Returns the one `fontSize` every selected character carries — `""` for text
 * without a size ("Normal") — or `null` when the selection mixes sizes, so the
 * toolbar can leave every size button unlit.
 *
 * - An empty selection (bare caret) follows the caret: the stored marks (a
 *   size picked for the next typed character) win over the marks before the
 *   caret, as in `getMarkAttributes`.
 * - Every range counts, not just `from`/`to`: a multi-cell `CellSelection`
 *   spans several ranges.
 * - A range without any text (e.g. only an image) falls back to the caret rule
 *   at its start.
 */
export function selectionFontSize(state: EditorState): string | null {
  const { selection } = state;
  if (selection.empty) {
    return fontSizeOf(state.storedMarks ?? selection.$head.marks());
  }
  const sizes = new Set<string>();
  for (const range of selection.ranges) {
    state.doc.nodesBetween(range.$from.pos, range.$to.pos, (node) => {
      if (node.isText) sizes.add(fontSizeOf(node.marks));
    });
  }
  if (sizes.size === 0) return fontSizeOf(selection.$from.marks());
  return sizes.size === 1 ? [...sizes][0]! : null;
}

function fontSizeOf(marks: readonly Mark[]): string {
  const size = marks.find((m) => m.type.name === "textStyle")?.attrs["fontSize"];
  return typeof size === "string" ? size : "";
}
